import { useEffect, useState, useRef } from 'react';

interface UseFloatingButtonOptions {
  showStartRef: React.RefObject<HTMLElement>;
  hideEndRef: React.RefObject<HTMLElement>;
  offset?: number;
}

/**
 * フローティングボタンの表示制御フック
 * 指定された要素間でスクロールしている時のみボタンを表示
 */
export const useFloatingButton = (options: UseFloatingButtonOptions) => {
  const { showStartRef, hideEndRef, offset = 100 } = options;
  const [isVisible, setIsVisible] = useState(false);
  const rafRef = useRef<number>();

  useEffect(() => {
    const handleScroll = () => {
      // RAF を使用してパフォーマンス最適化
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      rafRef.current = requestAnimationFrame(() => {
        const showStartElement = showStartRef.current;
        const hideEndElement = hideEndRef.current;

        if (!showStartElement || !hideEndElement) {
          return;
        }

        const scrollY = window.scrollY;
        const windowHeight = window.innerHeight;

        // 開始要素の位置を取得
        const showStartRect = showStartElement.getBoundingClientRect();
        const showStartTop = scrollY + showStartRect.top;

        // 終了要素の位置を取得
        const hideEndRect = hideEndElement.getBoundingClientRect();
        const hideEndTop = scrollY + hideEndRect.top;

        // HeroSectionが画面から出始めた時から表示開始
        const showThreshold = showStartTop + showStartRect.height - windowHeight + offset;
        
        // FAQSectionが見え始めた時に非表示
        const hideThreshold = hideEndTop - windowHeight + offset;

        // スクロール位置が表示範囲内かチェック
        const shouldShow = scrollY >= showThreshold && scrollY <= hideThreshold;

        setIsVisible(shouldShow);
      });
    };

    // 初期チェック
    handleScroll();

    // スクロールイベントリスナー
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [showStartRef, hideEndRef, offset]);

  return { isVisible };
};